import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@soroush/ui';
import { ApiError, apiFetch } from '../lib/api';
import type { ClubHome } from '../lib/club';
import { isoToJalali, todayIso } from '../lib/jalali';
import {
  isIsoDate,
  isLatinName,
  isValidMobile,
  isValidNationalCode,
  normalizeMobile,
} from '../lib/validation';
import { JalaliDateSelect } from '../components/JalaliDateSelect';

const ASSISTANCE = ['WCHR', 'WCHS', 'WCHC', 'DEAF', 'BLND', 'MAAS'] as const;
const MEALS = ['STANDARD', 'VEGETARIAN', 'HALAL', 'KOSHER', 'CHILD'] as const;

type Draft = {
  firstName: string;
  lastName: string;
  localFirstName: string;
  localLastName: string;
  gender: '' | 'M' | 'F';
  birthDate: string;
  nationality: 'IR' | 'OTHER';
  country: string;
  nationalId: string;
  passportNumber: string;
  passportExpiry: string;
  passportCountry: string;
  homeAirport: string;
  seatPreference: ClubHome['profile']['seatPreference'];
  mealPreference: ClubHome['profile']['mealPreference'];
  language: 'fa' | 'en';
  assistance: string;
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelation: string;
  marketingOptIn: boolean;
  email: string;
  phone: string;
};

function draftFrom(home: ClubHome): Draft {
  const profile = home.profile;
  const foreign = Boolean(profile.nationality && profile.nationality !== 'IR');
  return {
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    localFirstName: profile.localFirstName ?? '',
    localLastName: profile.localLastName ?? '',
    gender: profile.gender ?? '',
    birthDate: profile.birthDate ?? '',
    nationality: foreign ? 'OTHER' : 'IR',
    country: foreign ? (profile.nationality ?? '') : '',
    nationalId: profile.nationalId ?? '',
    passportNumber: profile.passportNumber ?? '',
    passportExpiry: profile.passportExpiry ?? '',
    passportCountry: profile.passportCountry ?? (foreign ? (profile.nationality ?? '') : ''),
    homeAirport: profile.homeAirport ?? '',
    seatPreference: profile.seatPreference ?? 'ANY',
    mealPreference: profile.mealPreference ?? 'STANDARD',
    language: profile.language ?? 'fa',
    assistance: profile.specialAssistance?.[0] ?? '',
    emergencyName: profile.emergencyName ?? '',
    emergencyPhone: profile.emergencyPhone ?? '',
    emergencyRelation: profile.emergencyRelation ?? '',
    marketingOptIn: profile.marketingOptIn ?? false,
    email: home.contact.email ?? '',
    phone: home.contact.phone ?? '',
  };
}

function emailOk(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function ClubProfileForm({
  home,
  onCancel,
  onSaved,
}: {
  home: ClubHome;
  onCancel: () => void;
  onSaved: (next: ClubHome) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(home));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const currentJy = useMemo(() => isoToJalali(todayIso())?.jy ?? 1405, []);
  const birthYears = useMemo(() => Array.from({ length: 100 }, (_, i) => currentJy - i), [currentJy]);
  const expiryYears = useMemo(() => Array.from({ length: 15 }, (_, i) => currentJy + i), [currentJy]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    const required = t('common.requiredField');
    if (!draft.firstName.trim() || !isLatinName(draft.firstName)) next.firstName = t('common.latinOnly');
    if (!draft.lastName.trim() || !isLatinName(draft.lastName)) next.lastName = t('common.latinOnly');
    if (!draft.gender) next.gender = required;
    if (!isIsoDate(draft.birthDate) || draft.birthDate >= todayIso()) next.birthDate = required;
    if (draft.nationality === 'IR') {
      if (!isValidNationalCode(draft.nationalId)) next.nationalId = t('common.invalidNationalCode');
    } else {
      if (!/^[A-Za-z]{2}$/.test(draft.country.trim())) next.country = t('common.invalidCountry');
      if (!/^[A-Za-z0-9]{5,20}$/.test(draft.passportNumber.trim())) next.passportNumber = required;
      if (!/^[A-Za-z]{2}$/.test((draft.passportCountry || draft.country).trim())) {
        next.passportCountry = t('common.invalidCountry');
      }
      if (!isIsoDate(draft.passportExpiry)) next.passportExpiry = required;
    }
    if (draft.homeAirport.trim() && !/^[A-Za-z]{3}$/.test(draft.homeAirport.trim())) {
      next.homeAirport = t('club.invalidAirport');
    }
    const email = draft.email.trim();
    const phone = draft.phone.trim();
    if (email && !emailOk(email)) next.email = t('auth.invalidIdentifier');
    if (phone && !isValidMobile(phone)) next.phone = t('common.invalidMobile');
    if ((!email || !emailOk(email)) && (!phone || !isValidMobile(phone))) {
      next.email = t('auth.needContact');
    }
    const named = draft.emergencyName.trim();
    const phoned = draft.emergencyPhone.trim();
    if (named && !phoned) next.emergencyPhone = required;
    if (phoned && !named) next.emergencyName = required;
    return next;
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setFormError(t('common.fixErrors'));
      return;
    }
    setFormError('');
    setSaving(true);
    const nationality = draft.nationality === 'IR' ? 'IR' : draft.country.trim().toUpperCase();
    try {
      const saved = await apiFetch<ClubHome>('/api/v1/ffp/me/profile', {
        method: 'PUT',
        body: JSON.stringify({
          firstName: draft.firstName.trim(),
          lastName: draft.lastName.trim(),
          localFirstName: draft.localFirstName.trim(),
          localLastName: draft.localLastName.trim(),
          gender: draft.gender,
          birthDate: draft.birthDate,
          nationality,
          nationalId: draft.nationality === 'IR' ? draft.nationalId.trim() : '',
          passportNumber: draft.passportNumber.trim(),
          passportExpiry: draft.passportExpiry,
          passportCountry: (draft.passportCountry || (draft.nationality === 'OTHER' ? draft.country : '')).trim(),
          homeAirport: draft.homeAirport.trim(),
          seatPreference: draft.seatPreference,
          mealPreference: draft.mealPreference,
          language: draft.language,
          specialAssistance: draft.assistance ? [draft.assistance] : [],
          emergencyName: draft.emergencyName.trim(),
          emergencyPhone: draft.emergencyPhone.trim(),
          emergencyRelation: draft.emergencyRelation.trim(),
          marketingOptIn: draft.marketingOptIn,
          email: draft.email.trim(),
          phone: draft.phone.trim() ? normalizeMobile(draft.phone) : '',
        }),
      });
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && Array.isArray(err.details)) {
        const fieldErrors: Record<string, string> = {};
        for (const item of err.details as { path?: string; message?: string }[]) {
          if (item.path && item.message) fieldErrors[item.path] = item.message;
        }
        if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      }
      setFormError(err instanceof Error ? err.message : t('common.errorGeneric'));
    } finally {
      setSaving(false);
    }
  }

  const err = (key: string) => errors[key];

  return (
    <form className="club-form" onSubmit={(event) => void onSubmit(event)}>
      <div className="club-form__grid">
        <Field label={t('common.firstNameLatin')} value={draft.firstName} error={err('firstName')} onChange={(value) => set('firstName', value)} />
        <Field label={t('common.lastNameLatin')} value={draft.lastName} error={err('lastName')} onChange={(value) => set('lastName', value)} />
        <Field label={t('club.localFirstName')} value={draft.localFirstName} onChange={(value) => set('localFirstName', value)} />
        <Field label={t('club.localLastName')} value={draft.localLastName} onChange={(value) => set('localLastName', value)} />
        <label className="sa-field">
          <span className="sa-label">{t('common.gender')}</span>
          <select className="sa-input" value={draft.gender} onChange={(e) => set('gender', e.target.value as Draft['gender'])}>
            <option value="">—</option>
            <option value="M">{t('common.male')}</option>
            <option value="F">{t('common.female')}</option>
          </select>
          {err('gender') ? <p className="club-form__error">{err('gender')}</p> : null}
        </label>
        <JalaliDateSelect
          id="club-birth"
          label={t('common.birthDate')}
          value={draft.birthDate}
          years={birthYears}
          error={err('birthDate')}
          onChange={(iso) => set('birthDate', iso)}
        />
      </div>

      <h3>{t('club.documents')}</h3>
      <div className="club-form__grid">
        <label className="sa-field">
          <span className="sa-label">{t('common.nationality')}</span>
          <select
            className="sa-input"
            value={draft.nationality}
            onChange={(e) => set('nationality', e.target.value as Draft['nationality'])}
          >
            <option value="IR">{t('common.iranian')}</option>
            <option value="OTHER">{t('common.nonIranian')}</option>
          </select>
        </label>
        {draft.nationality === 'IR' ? (
          <Field
            label={t('common.nationalCode')}
            value={draft.nationalId}
            error={err('nationalId')}
            dir="ltr"
            onChange={(value) => set('nationalId', value)}
          />
        ) : (
          <Field
            label={t('common.passportCountry')}
            value={draft.country}
            error={err('country')}
            dir="ltr"
            onChange={(value) => set('country', value.toUpperCase())}
          />
        )}
        <Field
          label={t('common.passportNumber')}
          value={draft.passportNumber}
          error={err('passportNumber')}
          dir="ltr"
          onChange={(value) => set('passportNumber', value.toUpperCase())}
        />
        <Field
          label={t('common.passportCountry')}
          value={draft.passportCountry}
          error={err('passportCountry')}
          dir="ltr"
          onChange={(value) => set('passportCountry', value.toUpperCase())}
        />
        <JalaliDateSelect
          id="club-passport-expiry"
          label={t('common.passportExpiry')}
          value={draft.passportExpiry}
          years={expiryYears}
          error={err('passportExpiry')}
          onChange={(iso) => set('passportExpiry', iso)}
        />
      </div>

      <h3>{t('club.contact')}</h3>
      <p className="sa-text-muted">{t('club.contactHint')}</p>
      <div className="club-form__grid">
        <Field label={t('common.email')} value={draft.email} error={err('email')} dir="ltr" onChange={(value) => set('email', value)} />
        <Field label={t('common.mobile')} value={draft.phone} error={err('phone')} dir="ltr" onChange={(value) => set('phone', value)} />
      </div>

      <h3>{t('club.preferences')}</h3>
      <div className="club-form__grid">
        <Field
          label={t('club.homeAirport')}
          value={draft.homeAirport}
          error={err('homeAirport')}
          dir="ltr"
          onChange={(value) => set('homeAirport', value.toUpperCase())}
        />
        <label className="sa-field">
          <span className="sa-label">{t('club.seat')}</span>
          <select
            className="sa-input"
            value={draft.seatPreference}
            onChange={(e) => set('seatPreference', e.target.value as Draft['seatPreference'])}
          >
            {(['ANY', 'WINDOW', 'AISLE'] as const).map((seat) => (
              <option key={seat} value={seat}>
                {t(`club.seats.${seat}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="sa-field">
          <span className="sa-label">{t('club.meal')}</span>
          <select
            className="sa-input"
            value={draft.mealPreference}
            onChange={(e) => set('mealPreference', e.target.value as Draft['mealPreference'])}
          >
            {MEALS.map((meal) => (
              <option key={meal} value={meal}>
                {t(`club.meals.${meal}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="sa-field">
          <span className="sa-label">{t('common.language')}</span>
          <select
            className="sa-input"
            value={draft.language}
            onChange={(e) => set('language', e.target.value as Draft['language'])}
          >
            <option value="fa">فارسی</option>
            <option value="en">English</option>
          </select>
        </label>
        <label className="sa-field">
          <span className="sa-label">{t('club.assistance')}</span>
          <select className="sa-input" value={draft.assistance} onChange={(e) => set('assistance', e.target.value)}>
            <option value="">{t('club.assistanceNone')}</option>
            {ASSISTANCE.map((code) => (
              <option key={code} value={code}>
                {t(`club.assistanceCodes.${code}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <h3>{t('club.emergency')}</h3>
      <div className="club-form__grid">
        <Field label={t('club.emergencyName')} value={draft.emergencyName} error={err('emergencyName')} onChange={(value) => set('emergencyName', value)} />
        <Field label={t('club.emergencyPhone')} value={draft.emergencyPhone} error={err('emergencyPhone')} dir="ltr" onChange={(value) => set('emergencyPhone', value)} />
        <Field label={t('club.emergencyRelation')} value={draft.emergencyRelation} onChange={(value) => set('emergencyRelation', value)} />
      </div>
      <label className="club-form__check">
        <input
          type="checkbox"
          checked={draft.marketingOptIn}
          onChange={(e) => set('marketingOptIn', e.target.checked)}
        />
        <span>{t('club.marketing')}</span>
      </label>

      {formError ? <div className="sa-alert sa-alert--error">{formError}</div> : null}
      <div className="club-form__actions">
        <Button type="submit" disabled={saving}>
          {saving ? t('common.loading') : t('club.save')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  dir,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  dir?: 'ltr';
}) {
  return (
    <label className={`sa-field${error ? ' has-error' : ''}`}>
      <span className="sa-label">{label}</span>
      <input className="sa-input" dir={dir} value={value} onChange={(e) => onChange(e.target.value)} />
      {error ? <p className="club-form__error">{error}</p> : null}
    </label>
  );
}
